import {
  isFeatureFace,
  isFeatureLine,
  isFeaturePoint,
  isSketchContour,
  isFeatureResultMesh,
  isRevolveRegionContourObject,
  isSketchArcObject,
  isSketchCircleObject,
  isSketchEllipseObject,
  isSketchLineObject,
  isSketchParabolaObject,
  isSketchPointObject,
  isSketchSplineObject,
  isSketchStyleSplineObject,
} from '../../../mesh'
import type { Object3D } from 'three'
import type { EntitySelectionMode } from '../EntitySelectionMode'

/** 实体候选稳定类型位；使用常量对象，不引入运行时枚举。 */
export const ENTITY_SELECTION_CANDIDATE_MASK = {
  /** 草图点。 */
  SketchPoint: 1 << 0,
  /** 特征点。 */
  FeaturePoint: 1 << 1,
  /** 草图线、圆、圆弧、椭圆、抛物线或样条。 */
  SketchLine: 1 << 2,
  /** 特征边。 */
  FeatureLine: 1 << 3,
  /** 草图轮廓面或旋转区域面。 */
  SketchMesh: 1 << 4,
  /** 特征面或特征结果体。 */
  FeatureMesh: 1 << 5,
} as const

/** 任一特征点、线或面候选位。 */
export const ENTITY_SELECTION_FEATURE_CANDIDATE_MASK = (
  ENTITY_SELECTION_CANDIDATE_MASK.FeaturePoint
  | ENTITY_SELECTION_CANDIDATE_MASK.FeatureLine
  | ENTITY_SELECTION_CANDIDATE_MASK.FeatureMesh
)

/**
 * 一次性解析对象全部稳定候选类型。
 *
 * 对象可能同时携带多个协议标记，因此使用位掩码而不是互斥字符串；
 * 这样既能在首次分类后缓存，也不会改变旧实现把同一对象放入多个候选域的兼容语义。
 *
 * @param object 已进入实体选择稳定候选集的对象。
 * @param classification 可选的首次分类记录；只在类型守卫成功后写入具体对象。
 * @returns 当前对象的稳定候选类型位。
 */
export function resolveEntitySelectionCandidateMask(object: Object3D, classification?: EntitySelectionCandidateClassification): number {
  let mask = 0
  if (isSketchPointObject(object)) {
    mask |= ENTITY_SELECTION_CANDIDATE_MASK.SketchPoint
    if (classification) classification.sketchPoint = object
  }
  if (isFeaturePoint(object)) {
    mask |= ENTITY_SELECTION_CANDIDATE_MASK.FeaturePoint
    if (classification) classification.featurePoint = object
  }
  if (
    isSketchLineObject(object)
    || isSketchCircleObject(object)
    || isSketchArcObject(object)
    || isSketchEllipseObject(object)
    || isSketchParabolaObject(object)
    || isSketchSplineObject(object)
    || isSketchStyleSplineObject(object)
  ) {
    mask |= ENTITY_SELECTION_CANDIDATE_MASK.SketchLine
    if (classification) classification.sketchLine = object
  }
  if (isFeatureLine(object)) {
    mask |= ENTITY_SELECTION_CANDIDATE_MASK.FeatureLine
    if (classification) classification.featureLine = object
  }
  if (isSketchContour(object) || isRevolveRegionContourObject(object)) {
    mask |= ENTITY_SELECTION_CANDIDATE_MASK.SketchMesh
  }
  if (isFeatureFace(object) || isFeatureResultMesh(object)) {
    mask |= ENTITY_SELECTION_CANDIDATE_MASK.FeatureMesh
  }
  return mask
}

/**
 * 判断稳定类型位是否允许进入当前选择模式的直接候选域。
 *
 * 特征面在 edge 模式用于确定前景边界的例外由分区器单独保留；本函数只回答直接模式资格。
 *
 * @param mask 对象稳定候选类型位。
 * @param mode 当前选择模式。
 * @returns 至少一个类型位允许直接参与当前模式时返回 true。
 */
export function isEntitySelectionCandidateMaskAllowed(
  mask: number,
  mode: EntitySelectionMode,
): boolean {
  if (mode === 'object' || mode === 'topology') return mask !== 0
  if (mode === 'vertex') {
    return (mask & (
      ENTITY_SELECTION_CANDIDATE_MASK.SketchPoint
      | ENTITY_SELECTION_CANDIDATE_MASK.FeaturePoint
    )) !== 0
  }
  if (mode === 'edge') {
    return (mask & (
      ENTITY_SELECTION_CANDIDATE_MASK.SketchLine
      | ENTITY_SELECTION_CANDIDATE_MASK.FeatureLine
    )) !== 0
  }
  if (mode === 'face') {
    return (mask & (
      ENTITY_SELECTION_CANDIDATE_MASK.SketchMesh
      | ENTITY_SELECTION_CANDIDATE_MASK.FeatureMesh
    )) !== 0
  }
  return (mask & (
    ENTITY_SELECTION_CANDIDATE_MASK.SketchLine
    | ENTITY_SELECTION_CANDIDATE_MASK.FeatureLine
    | ENTITY_SELECTION_CANDIDATE_MASK.SketchMesh
    | ENTITY_SELECTION_CANDIDATE_MASK.FeatureMesh
  )) !== 0
}
